import { TileType, EnemyType, EnemyGrade, DecorDef, DecorType } from '@/config/GameConfig';
import { ComponentType } from '@/components/ComponentDefs';
import { BehaviorRule, ConditionType, ActionType } from '@/behaviors/BehaviorDefs';


// ── Cinematic triggers ──
export interface CinematicTrigger {
  /** Registered cinematic sequence ID */
  sequenceId: string;
  /** When to trigger */
  event: 'mapStart' | 'waveStart' | 'waveEnd' | 'victory' | 'defeat' | 'tileEnter';
  /** For waveStart/waveEnd: which wave number (1-based). Omit = any wave. */
  waveNumber?: number;
  /** For tileEnter: which tile {col, row}. Triggers when a robot or enemy enters. */
  tile?: { col: number; row: number };
  /** Play only once per save slot (persisted in localStorage). Default: true */
  once?: boolean;
}

// ── Difficulty levels (10 tiers) ──
export type MapDifficulty =
  | 'Beginner' | 'Easy' | 'Normal' | 'Moderate'
  | 'Challenging' | 'Hard' | 'Very Hard' | 'Expert' | 'Nightmare' | 'Infernal';

// ── Map definition interface ──
export interface MapDefinition {
  id: string;
  name: string;
  difficulty: MapDifficulty;
  description: string;
  cols: number;
  rows: number;
  grid: TileType[][];
  // Waypoint paths (grid coords) — each path = [spawn, ...waypoints, base]
  paths: { col: number; row: number }[][];
  // Enemy types allowed on this map (progressive unlock)
  allowedEnemies: EnemyType[];
  // Enemy grades allowed (if omitted, determined by tier). E.g. ['elite','boss']
  allowedGrades?: EnemyGrade[];
  // Optional decorations (overlay elements on top of tiles)
  decorations?: DecorDef[];
  // Number of waves for this map (challenge mode). If omitted, uses WaveComposer default.
  waveCount?: number;
  // Power budget multiplier: 0=Trivial(0.3x), 1=Easy(0.6x), 2=Normal(1x), 3=Hard(1.8x), 4=Brutal(3x)
  intensity?: number;
  // If true, all waves get equal power budgets (no progressive ramp). Used for stress tests.
  flatWaveDistribution?: boolean;
  // Override spawn delay in ms (default 800). Lower = enemies spawn faster.
  spawnDelayMs?: number;
  // Cinematic triggers — fire cinematic sequences on game events
  cinematics?: CinematicTrigger[];
  // Pre-deployed robots: auto-placed at map start, skips workshop phase
  predeployedRobots?: {
    col: number;
    row: number;
    components: { type: ComponentType; lx: number; ly: number; rotation?: number; flipX?: boolean; flipY?: boolean; evoLevel?: number }[];
    rules: BehaviorRule[];
  }[];
}

const G = TileType.GROUND;
const W = TileType.WALL;
const P = TileType.PATH;
const Z = TileType.PLACEMENT;
const B = TileType.BASE;
const S = TileType.SPAWN;
const M = TileType.MUD;
const A = TileType.WATER;     // "Aqua" — deep water (drowns ground enemies)
const R = TileType.GRILLE;    // Grille placement (+15% fire rate)
const X = TileType.MAGMA;     // Magma (damage hazard)
const K = TileType.BROKEN_PLACEMENT; // Damaged placement zone

// Helper: pad row to target length with GROUND
function r(tiles: TileType[], cols: number): TileType[] {
  while (tiles.length < cols) tiles.push(G);
  return tiles.slice(0, cols);
}

// ═══════════════════════════════════════════════════════════════
// MAP 1: Prairie — 16×10, single straight corridor, very simple
// ═══════════════════════════════════════════════════════════════
const PRAIRIE_COLS = 16;
const PRAIRIE_ROWS = 10;
const prairieGrid: TileType[][] = [
  //0  1  2  3  4  5  6  7  8  9 10 11 12 13 14 15
  r([W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W], PRAIRIE_COLS), // 0
  r([W, G, G, Z, Z, G, G, Z, Z, G, G, Z, Z, G, G, W], PRAIRIE_COLS), // 1
  r([W, G, G, Z, Z, G, G, Z, Z, G, G, Z, Z, G, G, W], PRAIRIE_COLS), // 2
  r([W, G, G, Z, Z, G, G, Z, Z, G, G, Z, Z, G, G, W], PRAIRIE_COLS), // 3
  r([S, P, P, P, P, P, P, P, P, P, P, P, P, P, B, W], PRAIRIE_COLS), // 4  <- single corridor
  r([W, G, G, Z, Z, G, G, Z, Z, G, G, Z, Z, G, G, W], PRAIRIE_COLS), // 5
  r([W, G, G, Z, Z, G, G, Z, Z, G, G, Z, Z, G, G, W], PRAIRIE_COLS), // 6
  r([W, G, G, Z, Z, G, G, Z, Z, G, G, Z, Z, G, G, W], PRAIRIE_COLS), // 7
  r([W, G, G, G, G, G, G, G, G, G, G, G, G, G, G, W], PRAIRIE_COLS), // 8
  r([W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W], PRAIRIE_COLS), // 9
];

export const MAP_PRAIRIE: MapDefinition = {
  id: 'prairie',
  name: 'Prairie',
  difficulty: 'Beginner',
  description: 'Single corridor. Great for learning.',
  cols: PRAIRIE_COLS,
  rows: PRAIRIE_ROWS,
  grid: prairieGrid,
  paths: [
    [{ col: 0, row: 4 }, { col: 14, row: 4 }],
  ],
  allowedEnemies: [EnemyType.GRUNT, EnemyType.RUNNER],
  waveCount: 10,
  decorations: [
    { type: DecorType.TREE, col: 2, row: 1 },
    { type: DecorType.ROCHER, col: 6, row: 8 },
    { type: DecorType.SOUCHE, col: 10, row: 1 },
    { type: DecorType.FLAQUE, col: 13, row: 7 },
    { type: DecorType.BARREL, col: 5, row: 6 },
  ],
};

// ═══════════════════════════════════════════════════════════════
// MAP 2: Outpost — 16×10, two short parallel paths
// ═══════════════════════════════════════════════════════════════
const OUTPOST_COLS = 16;
const OUTPOST_ROWS = 10;
const outpostGrid: TileType[][] = [
  r([W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W], OUTPOST_COLS),
  r([W, G, Z, Z, G, G, Z, Z, G, G, Z, Z, G, G, G, W], OUTPOST_COLS),
  r([S, P, P, P, P, P, P, P, P, P, P, P, P, P, B, W], OUTPOST_COLS),
  r([W, G, Z, Z, G, W, W, G, G, W, W, G, Z, Z, G, W], OUTPOST_COLS),
  r([W, G, G, G, G, G, G, G, G, G, G, G, G, G, G, W], OUTPOST_COLS),
  r([W, G, G, G, G, G, G, G, G, G, G, G, G, G, G, W], OUTPOST_COLS),
  r([W, G, Z, Z, G, W, W, G, G, W, W, G, Z, Z, G, W], OUTPOST_COLS),
  r([S, P, P, P, P, P, P, P, P, P, P, P, P, P, B, W], OUTPOST_COLS),
  r([W, G, Z, Z, G, G, Z, Z, G, G, Z, Z, G, G, G, W], OUTPOST_COLS),
  r([W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W], OUTPOST_COLS),
];

export const MAP_OUTPOST: MapDefinition = {
  id: 'outpost',
  name: 'Outpost',
  difficulty: 'Easy',
  description: '2 short corridors. Swarms incoming.',
  cols: OUTPOST_COLS,
  rows: OUTPOST_ROWS,
  grid: outpostGrid,
  paths: [
    [{ col: 0, row: 2 }, { col: 14, row: 2 }],
    [{ col: 0, row: 7 }, { col: 14, row: 7 }],
  ],
  allowedEnemies: [EnemyType.GRUNT, EnemyType.RUNNER, EnemyType.SWARM],
  waveCount: 20,
  decorations: [
    { type: DecorType.BARREL, col: 3, row: 4 },
    { type: DecorType.CAISSE, col: 12, row: 4 },
    { type: DecorType.TREE, col: 8, row: 5 },
    { type: DecorType.DEAD_TREE, col: 14, row: 1 },
    { type: DecorType.FLAQUE, col: 7, row: 5 },
    { type: DecorType.CONDUIT, col: 10, row: 4 },
  ],
};

// ═══════════════════════════════════════════════════════════════
// MAP 3: Valley — 20×15, 2 parallel corridors (original map)
// ═══════════════════════════════════════════════════════════════
const VALLEE_COLS = 20;
const VALLEE_ROWS = 15;
const valleeGrid: TileType[][] = [
  r([W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W], VALLEE_COLS),
  r([W, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, W], VALLEE_COLS),
  r([W, G, Z, Z, G, W, W, G, Z, Z, G, W, W, G, Z, Z, G, M, G, W], VALLEE_COLS),
  r([S, P, P, P, P, P, P, P, P, P, P, P, P, P, P, P, P, P, B, W], VALLEE_COLS),
  r([W, G, Z, Z, G, W, W, G, Z, Z, G, W, W, G, Z, Z, G, M, G, W], VALLEE_COLS),
  r([W, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, W], VALLEE_COLS),
  r([W, W, W, W, G, W, W, W, W, G, A, W, W, W, W, G, W, W, W, W], VALLEE_COLS),
  r([W, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, W], VALLEE_COLS),
  r([W, G, Z, Z, G, W, W, G, Z, Z, G, W, W, G, Z, Z, G, G, G, W], VALLEE_COLS),
  r([W, G, Z, Z, G, M, G, G, Z, Z, G, G, G, G, Z, Z, G, G, G, W], VALLEE_COLS),
  r([S, P, P, P, P, P, P, P, P, P, P, P, P, P, P, P, P, P, B, W], VALLEE_COLS),
  r([W, G, Z, Z, G, M, G, G, Z, Z, G, G, G, G, Z, Z, G, G, G, W], VALLEE_COLS),
  r([W, G, Z, Z, G, W, W, G, Z, Z, G, W, W, G, Z, Z, G, G, G, W], VALLEE_COLS),
  r([W, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, W], VALLEE_COLS),
  r([W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W], VALLEE_COLS),
];

export const MAP_VALLEE: MapDefinition = {
  id: 'vallee',
  name: 'Valley',
  difficulty: 'Normal',
  description: '2 parallel corridors. The classic map.',
  cols: VALLEE_COLS,
  rows: VALLEE_ROWS,
  grid: valleeGrid,
  paths: [
    [{ col: 0, row: 3 }, { col: 18, row: 3 }],
    [{ col: 0, row: 10 }, { col: 18, row: 10 }],
  ],
  allowedEnemies: [EnemyType.GRUNT, EnemyType.RUNNER, EnemyType.SWARM, EnemyType.TANK],
  waveCount: 30,
  decorations: [
    { type: DecorType.TREE, col: 1, row: 1 },
    { type: DecorType.TREE, col: 18, row: 13 },
    { type: DecorType.ROCHER, col: 4, row: 7 },
    { type: DecorType.DEAD_TREE, col: 15, row: 1 },
    { type: DecorType.FLAQUE, col: 10, row: 7 },
    { type: DecorType.BARREL, col: 1, row: 5 },
    { type: DecorType.CAISSE, col: 18, row: 7 },
    { type: DecorType.CONNARDIUM, col: 15, row: 5 },
    { type: DecorType.SOUCHE, col: 1, row: 13 },
  ],
};

// ═══════════════════════════════════════════════════════════════
// MAP 4: Bridge — 18×12, single path with bottleneck
// ═══════════════════════════════════════════════════════════════
const BRIDGE_COLS = 18;
const BRIDGE_ROWS = 12;
const bridgeGrid: TileType[][] = [
  r([W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W], BRIDGE_COLS),
  r([W, G, G, Z, Z, G, G, A, W, W, A, G, G, Z, Z, G, G, W], BRIDGE_COLS),
  r([W, G, Z, Z, Z, G, G, A, W, W, A, G, G, Z, Z, Z, G, W], BRIDGE_COLS),
  r([S, P, P, P, P, P, P, M, W, W, M, P, P, P, P, P, B, W], BRIDGE_COLS),
  r([W, G, G, G, G, G, P, G, A, A, G, P, G, G, G, G, G, W], BRIDGE_COLS),
  r([W, W, W, W, W, G, P, P, P, P, P, P, G, W, W, W, W, W], BRIDGE_COLS),
  r([W, W, W, W, W, G, P, P, P, P, P, P, G, W, W, W, W, W], BRIDGE_COLS),
  r([W, G, G, G, G, G, P, G, A, A, G, P, G, G, G, G, G, W], BRIDGE_COLS),
  r([S, P, P, P, P, P, P, M, W, W, M, P, P, P, P, P, B, W], BRIDGE_COLS),
  r([W, G, Z, Z, Z, G, G, A, W, W, A, G, G, Z, Z, Z, G, W], BRIDGE_COLS),
  r([W, G, G, Z, Z, G, G, A, W, W, A, G, G, Z, Z, G, G, W], BRIDGE_COLS),
  r([W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W], BRIDGE_COLS),
];

export const MAP_BRIDGE: MapDefinition = {
  id: 'bridge',
  name: 'Bridge',
  difficulty: 'Moderate',
  description: 'Narrow bridge bottleneck. 2 paths merge at center.',
  cols: BRIDGE_COLS,
  rows: BRIDGE_ROWS,
  grid: bridgeGrid,
  paths: [
    [{ col: 0, row: 3 }, { col: 6, row: 3 }, { col: 6, row: 5 }, { col: 11, row: 5 }, { col: 11, row: 3 }, { col: 16, row: 3 }],
    [{ col: 0, row: 8 }, { col: 6, row: 8 }, { col: 6, row: 6 }, { col: 11, row: 6 }, { col: 11, row: 8 }, { col: 16, row: 8 }],
  ],
  allowedEnemies: [EnemyType.GRUNT, EnemyType.RUNNER, EnemyType.SWARM, EnemyType.TANK, EnemyType.PHANTOM],
  waveCount: 40,
  decorations: [
    { type: DecorType.BROKEN_WALL, col: 1, row: 1 },
    { type: DecorType.BARREL, col: 15, row: 1 },
    { type: DecorType.CONDUIT, col: 5, row: 4 },
    { type: DecorType.GARBAGE, col: 13, row: 7 },
    { type: DecorType.CAISSE, col: 1, row: 7 },
    { type: DecorType.ROCHER, col: 15, row: 10 },
    { type: DecorType.CONNARDIUM, col: 5, row: 7 },
  ],
};

// ═══════════════════════════════════════════════════════════════
// MAP 5: Canyon — 24×15, S-shaped serpentine path
// ═══════════════════════════════════════════════════════════════
const CANYON_COLS = 24;
const CANYON_ROWS = 15;
const canyonGrid: TileType[][] = [
  //0  1  2  3  4  5  6  7  8  9 10 11 12 13 14 15 16 17 18 19 20 21 22 23
  r([W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W], CANYON_COLS), // 0
  r([W, G, G, G, G, G, Z, Z, G, G, G, G, G, G, G, G, Z, Z, G, G, G, G, G, W], CANYON_COLS), // 1
  r([S, P, P, P, P, P, P, P, P, P, P, P, P, P, P, P, P, P, P, P, P, P, G, W], CANYON_COLS), // 2  → go right
  r([W, G, Z, Z, G, G, G, G, Z, Z, G, W, W, G, Z, Z, G, G, M, M, Z, P, G, W], CANYON_COLS), // 3
  r([W, G, G, G, G, W, W, G, G, G, G, W, W, G, G, G, G, W, W, G, G, P, G, W], CANYON_COLS), // 4
  r([W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, A, P, W, W], CANYON_COLS), // 5
  r([W, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, A, P, G, W], CANYON_COLS), // 6
  r([W, G, Z, P, P, P, P, P, P, P, P, P, P, P, P, P, P, P, P, P, P, P, G, W], CANYON_COLS), // 7  ← go left
  r([W, G, G, P, G, M, M, G, Z, Z, G, W, W, G, Z, Z, G, G, G, G, G, G, G, W], CANYON_COLS), // 8
  r([W, W, W, P, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W], CANYON_COLS), // 9
  r([W, G, A, P, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, Z, Z, Z, G, W], CANYON_COLS), // 10
  r([W, G, Z, P, P, P, P, P, P, P, P, P, P, P, P, P, P, P, P, P, P, B, G, W], CANYON_COLS), // 11 → go right to base
  r([W, G, A, G, G, G, Z, Z, G, G, G, G, G, G, Z, Z, G, G, Z, Z, Z, G, G, W], CANYON_COLS), // 12
  r([W, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, W], CANYON_COLS), // 13
  r([W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W], CANYON_COLS), // 14
];

export const MAP_CANYON: MapDefinition = {
  id: 'canyon',
  name: 'Canyon',
  difficulty: 'Challenging',
  description: 'S-shaped path. Enemies loop 3 times.',
  cols: CANYON_COLS,
  rows: CANYON_ROWS,
  grid: canyonGrid,
  paths: [
    [
      { col: 0, row: 2 },   // spawn
      { col: 21, row: 2 },  // go right
      { col: 21, row: 7 },  // turn down
      { col: 3, row: 7 },   // go left
      { col: 3, row: 11 },  // turn down
      { col: 21, row: 11 }, // go right to base
    ],
  ],
  allowedEnemies: [EnemyType.GRUNT, EnemyType.RUNNER, EnemyType.SWARM, EnemyType.TANK, EnemyType.PHANTOM, EnemyType.DRONE],
  waveCount: 50,
  decorations: [
    { type: DecorType.RUINS, col: 1, row: 1 },
    { type: DecorType.ROCHER, col: 22, row: 1 },
    { type: DecorType.DEAD_TREE, col: 1, row: 6 },
    { type: DecorType.CONNARDIUM, col: 10, row: 6 },
    { type: DecorType.GARBAGE, col: 15, row: 6 },
    { type: DecorType.BARREL, col: 22, row: 6 },
    { type: DecorType.BROKEN_WALL, col: 5, row: 13 },
    { type: DecorType.COLUMN, col: 15, row: 13 },
    { type: DecorType.CAISSE, col: 22, row: 13 },
    { type: DecorType.CONNARDIUM, col: 8, row: 13 },
  ],
};

// ═══════════════════════════════════════════════════════════════
// MAP 6: Crossroads — 22×14, two crossing paths
// ═══════════════════════════════════════════════════════════════
const CROSS_COLS = 22;
const CROSS_ROWS = 14;
const crossGrid: TileType[][] = [
  r([W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W], CROSS_COLS),
  r([W, G, G, Z, Z, G, G, G, G, A, G, G, G, A, G, G, G, Z, Z, G, G, W], CROSS_COLS),
  r([S, P, P, P, P, P, P, P, P, P, P, G, G, G, G, G, G, G, G, G, G, W], CROSS_COLS),
  r([W, G, Z, Z, G, G, G, G, G, G, P, G, G, G, G, G, G, G, R, R, G, W], CROSS_COLS),
  r([W, G, G, G, G, W, W, W, M, G, P, G, M, W, W, W, G, G, G, G, G, W], CROSS_COLS),
  r([W, G, G, G, G, G, G, G, G, G, P, G, G, G, G, G, G, G, G, Z, Z, W], CROSS_COLS),
  r([W, G, Z, Z, G, G, G, P, P, P, P, P, P, P, G, G, G, Z, Z, G, B, W], CROSS_COLS),
  r([W, G, Z, Z, G, G, G, P, P, P, P, P, P, P, G, G, G, Z, Z, G, B, W], CROSS_COLS),
  r([W, G, G, G, G, G, G, G, G, G, P, G, G, G, G, G, G, G, G, Z, Z, W], CROSS_COLS),
  r([W, G, G, G, G, W, W, W, M, G, P, G, M, W, W, W, G, G, G, G, G, W], CROSS_COLS),
  r([W, G, Z, Z, G, G, G, G, G, G, P, G, G, G, G, G, G, G, R, R, G, W], CROSS_COLS),
  r([S, P, P, P, P, P, P, P, P, P, P, G, G, G, G, G, G, G, G, G, G, W], CROSS_COLS),
  r([W, G, G, Z, Z, G, G, G, G, A, G, G, G, A, G, G, G, Z, Z, G, G, W], CROSS_COLS),
  r([W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W], CROSS_COLS),
];

export const MAP_CROSSROADS: MapDefinition = {
  id: 'crossroads',
  name: 'Crossroads',
  difficulty: 'Hard',
  description: '2 paths cross at center. Wasps swarm the skies.',
  cols: CROSS_COLS,
  rows: CROSS_ROWS,
  grid: crossGrid,
  paths: [
    [{ col: 0, row: 2 }, { col: 10, row: 2 }, { col: 10, row: 6 }, { col: 20, row: 6 }],
    [{ col: 0, row: 11 }, { col: 10, row: 11 }, { col: 10, row: 7 }, { col: 20, row: 7 }],
  ],
  allowedEnemies: [EnemyType.GRUNT, EnemyType.RUNNER, EnemyType.SWARM, EnemyType.TANK, EnemyType.PHANTOM, EnemyType.DRONE, EnemyType.WASP],
  waveCount: 60,
  decorations: [
    { type: DecorType.RUINS, col: 1, row: 1 },
    { type: DecorType.COLUMN, col: 20, row: 1 },
    { type: DecorType.CONNARDIUM, col: 1, row: 5 },
    { type: DecorType.GARBAGE, col: 1, row: 8 },
    { type: DecorType.BROKEN_WALL, col: 15, row: 5 },
    { type: DecorType.DEAD_TREE, col: 15, row: 8 },
    { type: DecorType.BARREL, col: 18, row: 5 },
    { type: DecorType.CONNARDIUM, col: 18, row: 8 },
    { type: DecorType.ROCHER, col: 1, row: 12 },
    { type: DecorType.GARBAGE, col: 20, row: 12 },
    { type: DecorType.BROKEN_STATUE, col: 11, row: 1 },
  ],
};

// ═══════════════════════════════════════════════════════════════
// MAP 7: Fortress — 26×17, 3 entry points converging to 1 base
// ═══════════════════════════════════════════════════════════════
const FORT_COLS = 26;
const FORT_ROWS = 17;
const fortGrid: TileType[][] = [
  //0  1  2  3  4  5  6  7  8  9 10 11 12 13 14 15 16 17 18 19 20 21 22 23 24 25
  r([W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W], FORT_COLS), // 0
  r([W, G, G, G, Z, Z, G, G, M, G, G, G, G, G, G, G, G, G, G, G, Z, Z, G, G, G, W], FORT_COLS), // 1
  r([S, P, P, P, P, P, P, P, P, P, G, W, W, W, W, G, A, G, G, G, G, G, G, G, G, W], FORT_COLS), // 2  spawn top
  r([W, G, Z, Z, G, G, G, G, G, P, G, W, G, G, W, G, R, R, G, G, G, G, G, G, G, W], FORT_COLS), // 3
  r([W, W, W, W, W, W, W, W, G, P, G, W, G, G, W, G, W, W, W, W, W, W, W, W, W, W], FORT_COLS), // 4
  r([W, G, G, G, G, G, G, G, G, P, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, W], FORT_COLS), // 5
  r([W, G, Z, Z, G, G, G, G, G, P, G, G, Z, Z, G, G, G, G, G, G, Z, Z, G, G, G, W], FORT_COLS), // 6
  r([W, G, G, G, G, G, G, G, G, P, P, P, P, P, P, P, P, P, P, P, P, P, P, P, B, W], FORT_COLS), // 7  ← merge → base
  r([S, P, P, P, P, P, P, P, P, P, G, G, Z, Z, G, G, G, G, G, G, Z, Z, G, G, G, W], FORT_COLS), // 8  spawn mid
  r([W, G, G, G, G, G, G, G, G, P, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, W], FORT_COLS), // 9
  r([W, G, Z, Z, G, G, G, G, G, P, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, W], FORT_COLS), // 10
  r([W, G, G, G, G, G, G, G, G, P, G, W, G, G, W, G, G, G, G, G, G, G, G, G, G, W], FORT_COLS), // 11
  r([W, W, W, W, W, W, W, W, G, P, G, W, W, W, W, G, W, W, W, W, W, W, W, W, W, W], FORT_COLS), // 12
  r([W, G, Z, Z, G, G, G, G, G, P, G, G, G, G, G, G, G, G, G, Z, Z, G, G, G, G, W], FORT_COLS), // 13
  r([S, P, P, P, P, P, P, P, P, P, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, W], FORT_COLS), // 14 spawn bottom
  r([W, G, G, G, Z, Z, G, G, G, G, G, G, G, G, G, G, G, G, G, G, Z, Z, G, G, G, W], FORT_COLS), // 15
  r([W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W], FORT_COLS), // 16
];

export const MAP_FORTERESSE: MapDefinition = {
  id: 'forteresse',
  name: 'Fortress',
  difficulty: 'Very Hard',
  description: '3 entrances converge to 1 base. Multi-front defense.',
  cols: FORT_COLS,
  rows: FORT_ROWS,
  grid: fortGrid,
  paths: [
    // Top path: spawn row2 → down to merge at row7 → right to base
    [{ col: 0, row: 2 }, { col: 9, row: 2 }, { col: 9, row: 7 }, { col: 24, row: 7 }],
    // Mid path: spawn row8 → right to merge at col9 → right to base
    [{ col: 0, row: 8 }, { col: 9, row: 8 }, { col: 9, row: 7 }, { col: 24, row: 7 }],
    // Bottom path: spawn row14 → up to merge at row7 → right to base
    [{ col: 0, row: 14 }, { col: 9, row: 14 }, { col: 9, row: 7 }, { col: 24, row: 7 }],
  ],
  allowedEnemies: [EnemyType.GRUNT, EnemyType.RUNNER, EnemyType.SWARM, EnemyType.TANK, EnemyType.PHANTOM, EnemyType.DRONE, EnemyType.WASP, EnemyType.BRUTE],
  waveCount: 70,
  decorations: [
    { type: DecorType.RUINS, col: 18, row: 1 },
    { type: DecorType.COLUMN, col: 12, row: 3 },
    { type: DecorType.BROKEN_WALL, col: 1, row: 5 },
    { type: DecorType.CONNARDIUM, col: 18, row: 5 },
    { type: DecorType.GARBAGE, col: 23, row: 5 },
    { type: DecorType.BARREL, col: 12, row: 5 },
    { type: DecorType.DEAD_TREE, col: 1, row: 9 },
    { type: DecorType.CONNARDIUM, col: 18, row: 9 },
    { type: DecorType.GARBAGE, col: 23, row: 9 },
    { type: DecorType.ROCHER, col: 1, row: 15 },
    { type: DecorType.BROKEN_STATUE, col: 18, row: 15 },
    { type: DecorType.CAISSE, col: 12, row: 11 },
  ],
};

// ═══════════════════════════════════════════════════════════════
// MAP 8: Siege — 26×16, 4 entry points converging to central base
// ═══════════════════════════════════════════════════════════════
const SIEGE_COLS = 26;
const SIEGE_ROWS = 16;
const siegeGrid: TileType[][] = [
  r([W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W], SIEGE_COLS),
  r([W, G, G, Z, Z, G, G, G, G, A, G, G, G, G, G, A, G, G, G, Z, Z, G, G, G, G, W], SIEGE_COLS),
  r([S, P, P, P, P, P, P, P, P, P, P, P, G, G, G, G, G, G, G, G, G, G, G, G, G, W], SIEGE_COLS),
  r([W, G, Z, Z, G, G, G, M, M, G, G, P, G, G, G, G, G, G, G, G, G, G, Z, Z, G, W], SIEGE_COLS),
  r([W, W, W, W, W, W, W, W, W, G, G, P, G, G, G, G, G, S, W, W, W, W, W, W, W, W], SIEGE_COLS),
  r([W, G, G, G, G, G, G, G, G, G, G, P, G, R, R, G, G, P, G, G, G, G, G, G, G, W], SIEGE_COLS),
  r([W, G, Z, Z, G, G, G, G, G, G, G, P, P, B, B, P, P, P, G, G, G, G, G, Z, Z, W], SIEGE_COLS),
  r([W, G, Z, Z, G, G, G, G, G, G, G, G, G, B, B, G, G, G, G, G, G, G, G, Z, Z, W], SIEGE_COLS),
  r([W, G, Z, Z, G, G, G, G, G, G, G, G, G, B, B, G, G, G, G, G, G, G, G, Z, Z, W], SIEGE_COLS),
  r([W, G, Z, Z, G, G, G, G, G, G, G, P, P, B, B, P, P, P, G, G, G, G, G, Z, Z, W], SIEGE_COLS),
  r([W, G, G, G, G, G, G, G, G, G, G, P, G, R, R, G, G, P, G, G, G, G, G, G, G, W], SIEGE_COLS),
  r([W, W, W, W, W, W, W, W, W, G, G, P, G, G, G, G, G, S, W, W, W, W, W, W, W, W], SIEGE_COLS),
  r([W, G, Z, Z, G, G, G, M, M, G, G, P, G, G, G, G, G, G, G, G, G, G, Z, Z, G, W], SIEGE_COLS),
  r([S, P, P, P, P, P, P, P, P, P, P, P, G, G, G, G, G, G, G, G, G, G, G, G, G, W], SIEGE_COLS),
  r([W, G, G, Z, Z, G, G, G, G, A, G, G, G, G, G, A, G, G, G, Z, Z, G, G, G, G, W], SIEGE_COLS),
  r([W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W], SIEGE_COLS),
];

export const MAP_SIEGE: MapDefinition = {
  id: 'siege',
  name: 'Siege',
  difficulty: 'Expert',
  description: '4 paths converge to a central base. Bombers inbound.',
  cols: SIEGE_COLS,
  rows: SIEGE_ROWS,
  grid: siegeGrid,
  paths: [
    // Top-left → center base
    [{ col: 0, row: 2 }, { col: 11, row: 2 }, { col: 11, row: 6 }, { col: 13, row: 6 }],
    // Bottom-left → center base
    [{ col: 0, row: 13 }, { col: 11, row: 13 }, { col: 11, row: 9 }, { col: 13, row: 9 }],
    // Top-right → center base
    [{ col: 17, row: 4 }, { col: 17, row: 6 }, { col: 14, row: 6 }],
    // Bottom-right → center base
    [{ col: 17, row: 11 }, { col: 17, row: 9 }, { col: 14, row: 9 }],
  ],
  allowedEnemies: [
    EnemyType.GRUNT, EnemyType.RUNNER, EnemyType.SWARM, EnemyType.TANK,
    EnemyType.PHANTOM, EnemyType.DRONE, EnemyType.WASP, EnemyType.BRUTE, EnemyType.BOMBER,
  ],
  waveCount: 80,
  decorations: [
    { type: DecorType.CONNARDIUM, col: 1, row: 5 },
    { type: DecorType.GARBAGE, col: 24, row: 5 },
    { type: DecorType.CONNARDIUM, col: 1, row: 10 },
    { type: DecorType.GARBAGE, col: 24, row: 10 },
    { type: DecorType.RUINS, col: 19, row: 1 },
    { type: DecorType.BROKEN_WALL, col: 6, row: 1 },
    { type: DecorType.COLUMN, col: 19, row: 14 },
    { type: DecorType.BROKEN_STATUE, col: 6, row: 14 },
    { type: DecorType.DEAD_TREE, col: 1, row: 1 },
    { type: DecorType.ROCHER, col: 24, row: 14 },
  ],
};

// ═══════════════════════════════════════════════════════════════
// MAP 9: Nexus — 30×20, large complex map with islands and 2 paths
// ═══════════════════════════════════════════════════════════════
const NEXUS_COLS = 30;
const NEXUS_ROWS = 20;
const nexusGrid: TileType[][] = [
  //0  1  2  3  4  5  6  7  8  9 10 11 12 13 14 15 16 17 18 19 20 21 22 23 24 25 26 27 28 29
  r([W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W], NEXUS_COLS), // 0
  r([W, G, G, G, G, G, G, Z, Z, G, G, A, A, G, G, G, G, G, A, A, G, G, Z, Z, G, G, G, G, G, W], NEXUS_COLS), // 1
  r([W, G, Z, Z, G, G, G, Z, Z, G, G, G, G, G, G, G, G, G, G, G, G, G, Z, Z, G, G, Z, Z, G, W], NEXUS_COLS), // 2
  r([S, P, P, P, P, P, P, P, P, P, P, P, P, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, W], NEXUS_COLS), // 3  spawn top
  r([W, G, Z, Z, G, G, G, G, G, G, G, M, P, G, G, W, W, W, W, G, G, G, G, G, G, G, Z, Z, G, W], NEXUS_COLS), // 4
  r([W, G, G, G, G, W, W, W, W, W, G, G, P, G, G, W, G, G, W, G, G, W, W, W, W, W, G, G, G, W], NEXUS_COLS), // 5
  r([W, G, G, G, G, W, G, G, G, W, G, G, P, G, G, G, G, G, G, G, G, W, G, G, G, W, G, G, G, W], NEXUS_COLS), // 6
  r([W, G, Z, Z, G, W, G, G, G, W, G, G, P, P, P, P, P, P, P, G, G, W, G, G, G, W, G, Z, G, W], NEXUS_COLS), // 7
  r([W, G, G, G, G, W, W, W, W, W, G, G, G, G, G, G, G, G, P, G, G, W, W, W, W, W, G, Z, Z, W], NEXUS_COLS), // 8
  r([W, G, G, G, G, G, G, G, G, G, G, Z, Z, G, G, X, G, G, P, P, P, P, P, P, P, P, P, P, B, W], NEXUS_COLS), // 9  → base (X = magma hazard)
  r([W, G, G, G, G, G, G, G, G, G, G, Z, Z, G, G, X, G, G, P, G, G, G, G, G, G, G, G, Z, Z, W], NEXUS_COLS), // 10
  r([W, G, G, G, G, W, W, W, W, W, G, G, G, G, G, G, G, G, P, G, G, W, W, W, W, W, G, G, G, W], NEXUS_COLS), // 11
  r([W, G, Z, Z, G, W, G, G, G, W, G, G, P, P, P, P, P, P, P, G, G, W, G, G, G, W, G, Z, G, W], NEXUS_COLS), // 12
  r([W, G, G, G, G, W, G, G, G, W, G, G, P, G, G, G, G, G, G, G, G, W, G, G, G, W, G, G, G, W], NEXUS_COLS), // 13
  r([W, G, G, G, G, W, W, W, W, W, G, G, P, G, G, W, G, G, W, G, G, W, W, W, W, W, G, G, G, W], NEXUS_COLS), // 14
  r([W, G, Z, Z, G, G, G, G, G, G, G, M, P, G, G, W, W, W, W, G, G, G, G, G, G, G, Z, Z, G, W], NEXUS_COLS), // 15
  r([S, P, P, P, P, P, P, P, P, P, P, P, P, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, W], NEXUS_COLS), // 16 spawn bottom
  r([W, G, Z, Z, G, G, G, Z, Z, G, G, G, G, G, G, G, G, G, G, G, G, G, Z, Z, G, G, Z, Z, G, W], NEXUS_COLS), // 17
  r([W, G, G, G, G, G, G, Z, Z, G, G, A, A, G, G, G, G, G, A, A, G, G, Z, Z, G, G, G, G, G, W], NEXUS_COLS), // 18
  r([W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W], NEXUS_COLS), // 19
];

export const MAP_NEXUS: MapDefinition = {
  id: 'nexus',
  name: 'Nexus',
  difficulty: 'Nightmare',
  description: 'Large map with islands. 2 paths converge at the center.',
  cols: NEXUS_COLS,
  rows: NEXUS_ROWS,
  grid: nexusGrid,
  paths: [
    // Top path: spawn row3 → down through center → right to base
    [
      { col: 0, row: 3 }, { col: 12, row: 3 }, { col: 12, row: 7 },
      { col: 18, row: 7 }, { col: 18, row: 9 }, { col: 28, row: 9 },
    ],
    // Bottom path: spawn row16 → up through center → right to base
    [
      { col: 0, row: 16 }, { col: 12, row: 16 }, { col: 12, row: 12 },
      { col: 18, row: 12 }, { col: 18, row: 9 }, { col: 28, row: 9 },
    ],
  ],
  allowedEnemies: [
    EnemyType.GRUNT, EnemyType.RUNNER, EnemyType.SWARM, EnemyType.TANK,
    EnemyType.PHANTOM, EnemyType.DRONE, EnemyType.WASP, EnemyType.BRUTE,
    EnemyType.BOMBER, EnemyType.SPECTER,
  ],
  waveCount: 90,
  decorations: [
    { type: DecorType.CONNARDIUM, col: 1, row: 1 },
    { type: DecorType.CONNARDIUM, col: 1, row: 18 },
    { type: DecorType.CONNARDIUM, col: 14, row: 1 },
    { type: DecorType.GARBAGE, col: 14, row: 18 },
    { type: DecorType.GARBAGE, col: 28, row: 1 },
    { type: DecorType.GARBAGE, col: 28, row: 18 },
    { type: DecorType.RUINS, col: 1, row: 6 },
    { type: DecorType.COLUMN, col: 1, row: 13 },
    { type: DecorType.BROKEN_WALL, col: 28, row: 6 },
    { type: DecorType.BROKEN_STATUE, col: 28, row: 13 },
    { type: DecorType.CHASM, col: 10, row: 1 },
    { type: DecorType.DEAD_TREE, col: 19, row: 1 },
    { type: DecorType.ROCHER, col: 10, row: 18 },
    { type: DecorType.BARREL, col: 19, row: 18 },
  ],
};

// ═══════════════════════════════════════════════════════════════
// MAP 10: Infernal — 40×30, massive labyrinth with 4 spawns, central base, winding paths
// ═══════════════════════════════════════════════════════════════
const INFERNAL_COLS = 40;
const INFERNAL_ROWS = 30;
const infernalGrid: TileType[][] = [
  // 0  1  2  3  4  5  6  7  8  9 10 11 12 13 14 15 16 17 18 19 20 21 22 23 24 25 26 27 28 29 30 31 32 33 34 35 36 37 38 39
  r([W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W], INFERNAL_COLS), // 0
  r([W, G, G, Z, Z, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, Z, Z, G, G, W], INFERNAL_COLS), // 1
  r([S, P, P, P, P, P, P, P, G, G, W, W, W, W, G, G, Z, Z, G, G, G, G, Z, Z, G, G, W, W, W, W, G, G, P, P, P, P, P, P, P, S], INFERNAL_COLS), // 2  spawn L + spawn R
  r([W, G, Z, Z, G, G, G, P, G, G, W, G, G, G, G, G, Z, Z, G, G, G, G, Z, Z, G, G, G, G, G, W, G, G, P, G, G, G, Z, Z, G, W], INFERNAL_COLS), // 3
  r([W, G, G, G, G, G, G, P, G, G, W, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, W, G, G, P, G, G, G, G, G, G, W], INFERNAL_COLS), // 4
  r([W, W, W, W, W, G, G, P, G, G, W, G, G, W, W, W, W, W, W, G, G, W, W, W, W, W, W, G, G, W, G, G, P, G, G, W, W, W, W, W], INFERNAL_COLS), // 5
  r([W, G, G, G, A, G, G, P, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, P, G, G, A, G, G, G, W], INFERNAL_COLS), // 6
  r([W, G, Z, Z, G, G, G, P, P, P, P, P, P, P, P, G, G, Z, Z, G, G, Z, Z, G, G, P, P, P, P, P, P, P, P, G, G, G, Z, Z, G, W], INFERNAL_COLS), // 7
  r([W, G, G, G, G, G, G, G, G, M, M, G, G, G, P, G, G, G, G, G, G, G, G, G, G, P, G, G, G, M, M, G, G, G, G, G, G, G, G, W], INFERNAL_COLS), // 8
  r([W, G, G, W, W, W, W, W, W, G, G, G, G, G, P, G, G, W, W, W, W, W, W, G, G, P, G, G, G, G, G, W, W, W, W, W, W, G, G, W], INFERNAL_COLS), // 9
  r([W, G, G, G, G, G, G, G, G, G, G, Z, Z, G, P, G, G, G, G, G, G, G, G, G, G, P, G, Z, Z, G, G, G, G, G, G, G, G, G, G, W], INFERNAL_COLS), // 10
  r([W, G, G, G, G, G, G, G, G, G, G, Z, Z, G, P, P, P, P, P, P, P, P, P, P, P, P, G, Z, Z, G, G, G, G, G, G, G, G, G, G, W], INFERNAL_COLS), // 11
  r([W, W, W, W, W, W, G, G, W, W, W, W, W, G, G, G, G, G, G, G, G, G, G, G, G, G, G, W, W, W, W, W, G, G, W, W, W, W, W, W], INFERNAL_COLS), // 12
  r([W, G, G, G, G, G, G, G, G, G, G, G, G, X, G, R, R, G, G, B, B, G, G, R, R, G, X, G, G, G, G, G, G, G, G, G, G, G, G, W], INFERNAL_COLS), // 13
  r([W, G, Z, Z, G, G, G, G, G, G, G, G, G, G, G, Z, Z, G, G, B, B, G, G, Z, Z, G, G, G, G, G, G, G, G, G, G, G, Z, Z, G, W], INFERNAL_COLS), // 14  base center
  r([W, G, Z, Z, G, G, G, G, G, G, G, G, G, G, G, Z, Z, G, G, B, B, G, G, Z, Z, G, G, G, G, G, G, G, G, G, G, G, Z, Z, G, W], INFERNAL_COLS), // 15
  r([W, G, G, G, G, G, G, G, G, G, G, G, G, X, G, R, R, G, G, B, B, G, G, R, R, G, X, G, G, G, G, G, G, G, G, G, G, G, G, W], INFERNAL_COLS), // 16
  r([W, W, W, W, W, W, G, G, W, W, W, W, W, G, G, G, G, G, G, G, G, G, G, G, G, G, G, W, W, W, W, W, G, G, W, W, W, W, W, W], INFERNAL_COLS), // 17
  r([W, G, G, G, G, G, G, G, G, G, G, Z, Z, G, P, P, P, P, P, P, P, P, P, P, P, P, G, Z, Z, G, G, G, G, G, G, G, G, G, G, W], INFERNAL_COLS), // 18
  r([W, G, G, G, G, G, G, G, G, G, G, Z, Z, G, P, G, G, G, G, G, G, G, G, G, G, P, G, Z, Z, G, G, G, G, G, G, G, G, G, G, W], INFERNAL_COLS), // 19
  r([W, G, G, W, W, W, W, W, W, G, G, G, G, G, P, G, G, W, W, W, W, W, W, G, G, P, G, G, G, G, G, W, W, W, W, W, W, G, G, W], INFERNAL_COLS), // 20
  r([W, G, G, G, G, G, G, G, G, M, M, G, G, G, P, G, G, G, G, G, G, G, G, G, G, P, G, G, G, M, M, G, G, G, G, G, G, G, G, W], INFERNAL_COLS), // 21
  r([W, G, Z, Z, G, G, G, P, P, P, P, P, P, P, P, G, G, Z, Z, G, G, Z, Z, G, G, P, P, P, P, P, P, P, P, G, G, G, Z, Z, G, W], INFERNAL_COLS), // 22
  r([W, G, G, G, A, G, G, P, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, P, G, G, A, G, G, G, W], INFERNAL_COLS), // 23
  r([W, W, W, W, W, G, G, P, G, G, W, G, G, W, W, W, W, W, W, G, G, W, W, W, W, W, W, G, G, W, G, G, P, G, G, W, W, W, W, W], INFERNAL_COLS), // 24
  r([W, G, G, G, G, G, G, P, G, G, W, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, W, G, G, P, G, G, G, G, G, G, W], INFERNAL_COLS), // 25
  r([W, G, Z, Z, G, G, G, P, G, G, W, G, G, G, G, G, Z, Z, G, G, G, G, Z, Z, G, G, G, G, G, W, G, G, P, G, G, G, Z, Z, G, W], INFERNAL_COLS), // 26
  r([S, P, P, P, P, P, P, P, G, G, W, W, W, W, G, G, Z, Z, G, G, G, G, Z, Z, G, G, W, W, W, W, G, G, P, P, P, P, P, P, P, S], INFERNAL_COLS), // 27  spawn L + spawn R
  r([W, G, G, Z, Z, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, G, Z, Z, G, G, W], INFERNAL_COLS), // 28
  r([W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W], INFERNAL_COLS), // 29
];

export const MAP_INFERNAL: MapDefinition = {
  id: 'infernal',
  name: 'Infernal',
  difficulty: 'Infernal',
  description: 'Massive labyrinth. 4 spawns converge on a central base. No mercy.',
  cols: INFERNAL_COLS,
  rows: INFERNAL_ROWS,
  grid: infernalGrid,
  paths: [
    // Top-left: spawn row2 col0 → right → down → center base
    [{ col: 0, row: 2 }, { col: 7, row: 2 }, { col: 7, row: 7 }, { col: 14, row: 7 },
     { col: 14, row: 11 }, { col: 19, row: 11 }, { col: 19, row: 13 }],
    // Top-right: spawn row2 col39 → left → down → center base
    [{ col: 39, row: 2 }, { col: 32, row: 2 }, { col: 32, row: 7 }, { col: 25, row: 7 },
     { col: 25, row: 11 }, { col: 20, row: 11 }, { col: 20, row: 13 }],
    // Bottom-left: spawn row27 col0 → right → up → center base
    [{ col: 0, row: 27 }, { col: 7, row: 27 }, { col: 7, row: 22 }, { col: 14, row: 22 },
     { col: 14, row: 18 }, { col: 19, row: 18 }, { col: 19, row: 16 }],
    // Bottom-right: spawn row27 col39 → left → up → center base
    [{ col: 39, row: 27 }, { col: 32, row: 27 }, { col: 32, row: 22 }, { col: 25, row: 22 },
     { col: 25, row: 18 }, { col: 20, row: 18 }, { col: 20, row: 16 }],
  ],
  allowedEnemies: [
    EnemyType.GRUNT, EnemyType.RUNNER, EnemyType.SWARM, EnemyType.TANK,
    EnemyType.PHANTOM, EnemyType.DRONE, EnemyType.WASP, EnemyType.BRUTE,
    EnemyType.BOMBER, EnemyType.SPECTER,
  ],
  waveCount: 120,
  decorations: [
    // Top-left quadrant
    { type: DecorType.CONNARDIUM, col: 1, row: 1 },
    { type: DecorType.GARBAGE, col: 4, row: 6 },
    { type: DecorType.RUINS, col: 1, row: 6 },
    { type: DecorType.BARREL, col: 4, row: 8 },
    // Top-right quadrant
    { type: DecorType.CONNARDIUM, col: 38, row: 1 },
    { type: DecorType.GARBAGE, col: 35, row: 6 },
    { type: DecorType.BROKEN_WALL, col: 38, row: 6 },
    { type: DecorType.CAISSE, col: 35, row: 8 },
    // Center area (around base)
    { type: DecorType.CONNARDIUM, col: 15, row: 13 },
    { type: DecorType.CONNARDIUM, col: 24, row: 13 },
    { type: DecorType.GARBAGE, col: 15, row: 16 },
    { type: DecorType.GARBAGE, col: 24, row: 16 },
    { type: DecorType.COLUMN, col: 7, row: 13 },
    { type: DecorType.BROKEN_STATUE, col: 32, row: 13 },
    // Bottom-left quadrant
    { type: DecorType.CONNARDIUM, col: 1, row: 28 },
    { type: DecorType.DEAD_TREE, col: 4, row: 23 },
    { type: DecorType.ROCHER, col: 1, row: 21 },
    // Bottom-right quadrant
    { type: DecorType.GARBAGE, col: 38, row: 28 },
    { type: DecorType.CHASM, col: 35, row: 23 },
    { type: DecorType.BROKEN_WALL, col: 38, row: 21 },
  ],
};

// ═══════════════════════════════════════════════════════════════
// STRESS TEST: 30×20 arena, 3 paths, 200+ enemies, pre-deployed robots
// FPS benchmark map — all enemy types, massive waves, no workshop needed
// ═══════════════════════════════════════════════════════════════
const ST_COLS = 30;
const ST_ROWS = 20;
// Grid: 3 horizontal corridors (rows 4, 10, 16) with placement zones between them
const stressGrid: TileType[][] = (() => {
  const grid: TileType[][] = [];
  for (let row = 0; row < ST_ROWS; row++) {
    const line: TileType[] = [];
    for (let col = 0; col < ST_COLS; col++) {
      // Border walls
      if (row === 0 || row === ST_ROWS - 1 || col === ST_COLS - 1) {
        line.push(W);
      }
      // Spawn column (col 0)
      else if (col === 0 && (row === 4 || row === 10 || row === 16)) {
        line.push(S);
      }
      else if (col === 0) {
        line.push(W);
      }
      // Base column (col 28)
      else if (col === ST_COLS - 2 && (row === 4 || row === 10 || row === 16)) {
        line.push(B);
      }
      // Path corridors (rows 4, 10, 16)
      else if (row === 4 || row === 10 || row === 16) {
        line.push(P);
      }
      // Placement zones (rows adjacent to paths)
      else if (row >= 2 && row <= 6 && row !== 4) {
        line.push(Z);
      }
      else if (row >= 8 && row <= 12 && row !== 10) {
        line.push(Z);
      }
      else if (row >= 14 && row <= 18 && row !== 16) {
        line.push(Z);
      }
      // Fill
      else {
        line.push(G);
      }
    }
    grid.push(line);
  }
  return grid;
})();

// CS = COMPONENT_SPACING (20px) — local offset unit for robot grid cells
const CS = 20;

// Shoot-on-sight turret behavior
const turretRules: BehaviorRule[] = [
  { priority: 0, condition: ConditionType.ENEMY_DETECTED, action: ActionType.SHOOT },
  { priority: 1, condition: ConditionType.ALWAYS, action: ActionType.HOLD_POSITION },
];

// Support robot behavior: repair allies > recharge allies > repair base > hold
const repairRules: BehaviorRule[] = [
  { priority: 0, condition: ConditionType.ALLY_NEARBY_DAMAGED, action: ActionType.REPAIR_ALLY },
  { priority: 1, condition: ConditionType.BASE_DAMAGED, action: ActionType.REPAIR_BASE },
  { priority: 2, condition: ConditionType.ALWAYS, action: ActionType.HOLD_POSITION },
];
const rechargeRules: BehaviorRule[] = [
  { priority: 0, condition: ConditionType.ALLY_LOW_ENERGY, action: ActionType.RECHARGE_ALLY },
  { priority: 1, condition: ConditionType.ALWAYS, action: ActionType.HOLD_POSITION },
];

// Pre-deploy 15 realistic turrets: 5 per corridor, varied builds
// Each robot uses proper CS-based offsets and realistic component layouts
const stressRobots: NonNullable<MapDefinition['predeployedRobots']> = [];

// ── Build templates (realistic turret designs) ──

/** Light turret: structure + sensor + weapon + battery (compact cross) */
function lightTurret(col: number, row: number, weapon: ComponentType) {
  stressRobots.push({
    col, row,
    components: [
      { type: ComponentType.STRUCTURE, lx: 0, ly: 0 },
      { type: ComponentType.SENSOR_SHORT, lx: 0, ly: -CS },
      { type: weapon, lx: CS, ly: 0 },
      { type: ComponentType.BATTERY, lx: -CS, ly: 0 },
    ],
    rules: turretRules,
  });
}

/** Medium turret: structure + armor + sensor_medium + weapon + battery (T-shape) */
function mediumTurret(col: number, row: number, weapon: ComponentType) {
  stressRobots.push({
    col, row,
    components: [
      { type: ComponentType.STRUCTURE, lx: 0, ly: 0 },
      { type: ComponentType.ARMOR, lx: -CS, ly: 0 },
      { type: ComponentType.SENSOR_MEDIUM, lx: 0, ly: -CS },
      { type: weapon, lx: CS, ly: 0 },
      { type: ComponentType.BATTERY, lx: 0, ly: CS },
    ],
    rules: turretRules,
  });
}

/** Heavy turret: double structure + armor + sensor + weapon + battery (wide) */
function heavyTurret(col: number, row: number, weapon: ComponentType) {
  stressRobots.push({
    col, row,
    components: [
      { type: ComponentType.STRUCTURE, lx: 0, ly: 0 },
      { type: ComponentType.STRUCTURE, lx: -CS, ly: 0 },
      { type: ComponentType.ARMOR, lx: CS, ly: 0 },
      { type: ComponentType.ARMOR, lx: 0, ly: CS },
      { type: ComponentType.SENSOR_MEDIUM, lx: -CS, ly: -CS },
      { type: weapon, lx: CS, ly: -CS },
      { type: ComponentType.BATTERY, lx: -CS, ly: CS },
    ],
    rules: turretRules,
  });
}

/** Repair bot: structure + repair module + battery (stays behind turrets) */
function repairBot(col: number, row: number) {
  stressRobots.push({
    col, row,
    components: [
      { type: ComponentType.STRUCTURE, lx: 0, ly: 0 },
      { type: ComponentType.REPAIR_MODULE, lx: CS, ly: 0 },
      { type: ComponentType.BATTERY, lx: -CS, ly: 0 },
      { type: ComponentType.SENSOR_SHORT, lx: 0, ly: -CS },
    ],
    rules: repairRules,
  });
}

/** Recharge bot: structure + recharge module + battery (stays behind turrets) */
function rechargeBot(col: number, row: number) {
  stressRobots.push({
    col, row,
    components: [
      { type: ComponentType.STRUCTURE, lx: 0, ly: 0 },
      { type: ComponentType.RECHARGE_MODULE, lx: CS, ly: 0 },
      { type: ComponentType.BATTERY, lx: -CS, ly: 0 },
      { type: ComponentType.BATTERY, lx: 0, ly: CS },
    ],
    rules: rechargeRules,
  });
}

// ── Programmatic placement: 80 robots across 3 corridors ──
// Weapon rotation: mixed ground, AA, and dual-purpose
const groundWeapons: ComponentType[] = [
  ComponentType.CANNON_LIGHT, ComponentType.CANNON_HEAVY,
  ComponentType.FLAMETHROWER, ComponentType.MISSILE_SOL_SOL,
];
const antiAirWeapons: ComponentType[] = [
  ComponentType.MISSILE_SAM,
];
const dualWeapons: ComponentType[] = [
  ComponentType.BURST_CANNON, ComponentType.LASER,
];
const allWeapons = [...groundWeapons, ...dualWeapons, ...antiAirWeapons];

// 3 corridors: path rows 4/10/16, placement rows above/below each
const corridors = [
  { rows: [2, 3, 5, 6] },   // corridor 1 (path row 4)
  { rows: [8, 9, 11, 12] },  // corridor 2 (path row 10)
  { rows: [14, 15, 17, 18] }, // corridor 3 (path row 16)
];

// Place ~27 robots per corridor (24 turrets + 3 support = 81 total)
let weaponIdx = 0;
for (const corr of corridors) {
  const { rows } = corr;
  // Turrets: cols 2-27, staggered across 4 placement rows
  const turretSlots: { col: number; row: number }[] = [];
  for (let col = 2; col <= 27; col++) {
    turretSlots.push({ col, row: rows[col % 4] });
  }
  // Place turrets: cycle through weapon types, alternate light/medium/heavy
  const builders = [lightTurret, mediumTurret, heavyTurret];
  for (let i = 0; i < turretSlots.length; i++) {
    const { col, row } = turretSlots[i];
    const builder = builders[i % 3];
    const weapon = allWeapons[weaponIdx % allWeapons.length];
    builder(col, row, weapon);
    weaponIdx++;
  }
  // Support bots: 2 repair + 1 recharge per corridor, back line (col 28)
  repairBot(28, rows[0]);
  repairBot(28, rows[2]);
  rechargeBot(28, rows[1]);
}

export const MAP_STRESS_TEST: MapDefinition = {
  id: 'stress_test',
  name: 'STRESS TEST',
  difficulty: 'Infernal',
  description: 'FPS benchmark: 3 corridors, 200+ enemies, 80+ pre-deployed robots (turrets + support). Just press START!',
  cols: ST_COLS,
  rows: ST_ROWS,
  grid: stressGrid,
  paths: [
    // Path 1: top corridor
    Array.from({ length: ST_COLS - 2 }, (_, i) => ({ col: i, row: 4 })),
    // Path 2: middle corridor
    Array.from({ length: ST_COLS - 2 }, (_, i) => ({ col: i, row: 10 })),
    // Path 3: bottom corridor
    Array.from({ length: ST_COLS - 2 }, (_, i) => ({ col: i, row: 16 })),
  ],
  allowedEnemies: [
    EnemyType.GRUNT, EnemyType.RUNNER, EnemyType.SWARM, EnemyType.TANK,
    EnemyType.PHANTOM, EnemyType.DRONE, EnemyType.WASP, EnemyType.BRUTE,
    EnemyType.BOMBER, EnemyType.SPECTER,
  ],
  waveCount: 10,
  flatWaveDistribution: true,
  spawnDelayMs: 80,
  predeployedRobots: stressRobots,
};

// ═══════════════════════════════════════════════════════════════
// All maps in order (10 maps, progressive difficulty + stress test)
// ═══════════════════════════════════════════════════════════════
export const ALL_MAPS: MapDefinition[] = [
  MAP_PRAIRIE,     // 1. Beginner   — Grunt, Runner
  MAP_OUTPOST,     // 2. Easy       — + Swarm
  MAP_VALLEE,      // 3. Normal     — + Tank
  MAP_BRIDGE,      // 4. Moderate   — + Phantom
  MAP_CANYON,      // 5. Challenging — + Drone
  MAP_CROSSROADS,  // 6. Hard       — + Wasp
  MAP_FORTERESSE,  // 7. Very Hard  — + Brute
  MAP_SIEGE,       // 8. Expert     — + Bomber
  MAP_NEXUS,       // 9. Nightmare  — + Specter (all enemies)
  MAP_INFERNAL,    // 10. Infernal  — 40×30 labyrinth, 4 spawns, 120 waves
  MAP_STRESS_TEST, // 11. STRESS TEST — FPS benchmark, pre-deployed robots
];

